/**
 * User Management Service
 * Single API source for user/role/permission operations (STLDEV-1303)
 */
import { commonServerInstance } from '@/utilities/instances'

// --- Types ---

export interface IUserRole {
  role_id: number
  role_name: string
}

export interface IUser {
  id: number
  username: string
  email?: string
  department?: string
  location?: string
  customer?: number | number[]
  roles: IUserRole[]
}

export interface IRole {
  id: number
  name: string
  title: string
  abilities: number[]
}

export interface IAbility {
  id: number
  name: string
  title: string
  parent_id: number
  subitems?: IAbility[]
}

export interface IUserPermission {
  permission_id: number
}

export interface CreateUserParams {
  username: string
  password: string
  email?: string
  email_notice?: number
  department?: string
  location?: string
  partner_id?: string
  customer?: string
  is_active?: number
  [key: string]: unknown
}

export interface UpdateUserProfileParams {
  id: number
  department: string
  location: string
}

// --- User APIs ---

export const fetchAbilityList = async () => {
  const response = await commonServerInstance.get('/auth/permissions')
  return response?.data?.data
}

export const fetchAbilityListByUser = async (userId: number) => {
  const response = await commonServerInstance.get(
    `auth/user-permissions?id=${userId}`
  )
  return response?.data?.data as IUserPermission[]
}

export const fetchRoleList = async () => {
  const response = await commonServerInstance.get('/auth/roles')
  return response?.data?.data
}

export const fetchUserList = async () => {
  const response = await commonServerInstance.get('/auth/users')
  return response?.data?.data
}

export const fetchUserListByRole = async (roleId: number) => {
  const response = await commonServerInstance.get(
    `auth/roles/info?role_id=${roleId}`
  )
  return response?.data?.data
}

/**
 * Every user id currently holding delegated AC authority, across all teams
 * (STLDEV-1557). Used to badge rows in the user list for audit.
 *
 * Display only: delegations are created and ended from Delegation Management by
 * the AC, and these are only used for badge rows in the User Management page.
 */
export const fetchActiveDelegateIds = async (): Promise<number[]> => {
  if (import.meta.env.VITE_APP_COUNTRY === 'CA') {
    return []
  }
  const response = await commonServerInstance.get('/auth/delegates')
  return response?.data?.data?.delegate_user_ids ?? []
}

export const createUser = async (params: CreateUserParams) => {
  const response = await commonServerInstance.post('/auth/users/create', params)
  return response?.data
}

export const deleteUser = async (userId: number) => {
  const response = await commonServerInstance.post(
    `auth/users/delete?id=${userId}`
  )
  return response?.data?.data
}

export const updateUserRoles = async (userId: number, roleIds: string) => {
  const response = await commonServerInstance.post('auth/user-roles/update', {
    userId,
    roleIds
  })
  return response?.data?.data
}

export const updateUserInformation = async (
  userId: number,
  customer: string
) => {
  const response = await commonServerInstance.post(
    `auth/users/update?id=${userId}`,
    { customer }
  )
  return response?.data?.data
}

export const updateUserProfile = async (params: UpdateUserProfileParams) => {
  const response = await commonServerInstance.post(
    '/auth/users/update-info',
    params
  )
  return response?.data?.data
}

// --- Role APIs ---

export const createNewRole = async (name: string) => {
  const response = await commonServerInstance.post(
    `auth/roles/create?name=${name}`,
    { title: name }
  )
  return response?.data?.data
}

export const deleteRole = async (roleId: number) => {
  const response = await commonServerInstance.post(
    `auth/roles/delete?id=${roleId}`
  )
  return response?.data?.data
}

export const updateRole = async (roleId: number, name: string) => {
  const response = await commonServerInstance.post(
    `auth/roles/update?id=${roleId}`,
    { name }
  )
  return response?.data?.data
}

export const updateRoleAbilities = async (
  roleId: number,
  abilities: number[]
) => {
  const response = await commonServerInstance.post(
    'auth/role-permissions/update',
    {
      permission_ids: abilities,
      role_id: roleId
    }
  )
  return response?.data?.data
}

// --- Team APIs (US) ---

export const fetchUserTeamInfo = async (params: { id: number }) => {
  const response = await commonServerInstance.get('/settle/team/user-team', {
    params
  })
  return response?.data?.data
}

export const updateUserTeamInfo = async (params: {
  user_id: number
  team_id: number
  team_role: string
}) => {
  const response = await commonServerInstance.post(
    '/settle/team/assign-user-team',
    params
  )
  return response?.data?.data
}
